import { Box, Chip, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FlagIcon from '@mui/icons-material/Flag'
import BlockIcon from '@mui/icons-material/Block'
import WarningIcon from '@mui/icons-material/Warning'
import { useTranslation } from 'react-i18next'
import { CollapsibleSection } from '../CollapsibleSection'
import styles from './styles.module.scss'

export const RulesExecution = ({ metadata }) => {
  const { t } = useTranslation(['common'])

  const rulesExecution = metadata?.rulesExecution

  if (!rulesExecution) {
    return null
  }

  const getActionIcon = (action) => {
    switch (action) {
      case 'FLAG':
        return <FlagIcon fontSize="small" />
      case 'REJECT':
        return <BlockIcon fontSize="small" />
      case 'WARN':
        return <WarningIcon fontSize="small" />
      default:
        return null
    }
  }

  const getActionColor = (action) => {
    switch (action) {
      case 'FLAG':
        return '#ffc107'
      case 'REJECT':
        return '#dc3545'
      case 'WARN':
        return '#ff9800'
      default:
        return '#6c757d'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return '#00B85F'
      case 'REJECTED':
        return '#dc3545'
      default:
        return '#ffc107'
    }
  }

  const statusLabel = rulesExecution.status === 'APPROVED' ? t('common:approved') : rulesExecution.status === 'REJECTED' ? t('common:declined') : rulesExecution.status
  const hitCount = rulesExecution.hitRules?.length || 0
  const summary = `${t('common:status')}: ${statusLabel} - ${hitCount} ${t('common:hitRules')}`

  return (
    <CollapsibleSection title={t('common:rulesExecution')} summary={summary}>

      <Box className={styles.statusSection}>
        <Typography variant="subtitle2" color="textSecondary">
          {t('common:overallStatus')}
        </Typography>
        <Chip
          icon={rulesExecution.status === 'APPROVED' ? <CheckCircleIcon /> : <CancelIcon />}
          label={
            rulesExecution.status === 'APPROVED'
              ? t('common:approved')
              : rulesExecution.status === 'REJECTED'
              ? t('common:declined')
              : rulesExecution.status
          }
          sx={{
            backgroundColor: getStatusColor(rulesExecution.status),
            color: '#FFFFFF',
            fontWeight: 500,
            '& .MuiChip-icon': {
              color: '#FFFFFF',
            },
          }}
        />
      </Box>

      {rulesExecution.hitRules && rulesExecution.hitRules.length > 0 && (
        <Box className={styles.section}>
          <Typography variant="h6" mb={2}>
            {t('common:hitRules')} ({rulesExecution.hitRules.length})
          </Typography>

          <Box className={styles.rulesContainer}>
            {rulesExecution.hitRules.map((ruleItem, index) => {
              const rule = ruleItem.hitRule || ruleItem.executedRule
              return (
                <Accordion key={index} className={styles.ruleAccordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                    <Box className={styles.ruleHeader}>
                      <Box className={styles.ruleTitle}>
                        <Chip
                          icon={getActionIcon(rule.ruleAction)}
                          label={rule.ruleAction || 'N/A'}
                          size="small"
                          sx={{
                            backgroundColor: getActionColor(rule.ruleAction),
                            color: '#FFFFFF',
                            fontWeight: 500,
                            marginRight: 1,
                            '& .MuiChip-icon': {
                              color: '#FFFFFF',
                            },
                          }}
                        />
                        <Typography variant="subtitle1" fontWeight={600}>
                          {rule.ruleName}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {rule.ruleId}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={styles.accordionDetails}>
                    <Typography variant="body2">{rule.ruleDescription}</Typography>
                  </AccordionDetails>
                </Accordion>
              )
            })}
          </Box>
        </Box>
      )}

      {rulesExecution.executedRules && rulesExecution.executedRules.length > 0 && (
        <Box className={styles.section}>
          <Typography variant="subtitle2" color="textSecondary" mt={2}>
            {t('common:totalExecutedRules')}: {rulesExecution.executedRules.length}
          </Typography>
        </Box>
      )}
    </CollapsibleSection>
  )
}
